import { createAutosuggest } from "~/lib/listings/create-autosuggest.svelte";
import type { Autosuggest, AutosuggestConfig } from "~/lib/listings/create-autosuggest.svelte";
import { mapProductsToCards } from "~/lib/bloomreach/transformers";
import { searchUrl } from "~/lib/url-state";
import type { ProductCardProductProps } from "~/components/product/ProductCard/types";

/**
 * One row of the panel, in the order it renders and arrow keys traverse it. Every option
 * in both shells takes its id from here, so aria-activedescendant can never name an
 * element that isn't rendered (2026-09-01 audit, §1.3).
 */
export type AutosuggestItem =
  | { kind: "suggestion"; id: string; term: string }
  | { kind: "product"; id: string; product: ProductCardProductProps; href: string }
  | { kind: "showAll"; id: string; href: string };

const ITEM_ID_PREFIX = "da-autosuggest-item-";

export interface SearchAutosuggestCoreOptions {
  config: AutosuggestConfig;
  /** Injectable for tests/stories — defaults to a real createAutosuggest(config). */
  autosuggest?: Autosuggest;
}

export function createSearchAutosuggestCore(options: SearchAutosuggestCoreOptions) {
  const { config } = options;
  const autosuggest = options.autosuggest ?? createAutosuggest(config);

  let _highlightedIndex: number = $state(-1);

  // How many product cards the mounted shell has room for, set once it has measured
  // itself. `undefined` means nothing has measured yet and every card is offered.
  //
  // The cap lives here rather than in the shells because `items` is what
  // aria-activedescendant names: a card hidden by CSS would still be an item, and the
  // highlight would land on a box nobody can see — which axe passes, since the id
  // resolves.
  let _visibleProductCount: number | undefined = $state(undefined);

  // How many query suggestions the mounted shell shows — five text rows in the desktop
  // column, three pills across a phone, against however many Bloomreach returns. Capped
  // here for the same reason as the product count above.
  let _visibleSuggestionCount: number | undefined = $state(undefined);

  // Escape and outside-clicks hide the panel without touching the query; the next
  // keystroke re-arms it.
  let _dismissed = $state(false);

  const cards = $derived(mapProductsToCards(autosuggest.products));

  // Products lead on both breakpoints, then "Show all results" (which belongs to the
  // product block visually), then query suggestions. Both shells render in exactly this
  // order — the ids are shared, so DOM order has to match it (WCAG 1.3.2).
  const items = $derived.by<AutosuggestItem[]>(() => {
    const allSuggestions = autosuggest.querySuggestions;
    const suggestions =
      _visibleSuggestionCount === undefined
        ? allSuggestions
        : allSuggestions.slice(0, _visibleSuggestionCount);
    // Otherwise arrowing an empty panel highlights a row nothing renders.
    if (suggestions.length === 0 && cards.length === 0) return [];
    const visibleCards =
      _visibleProductCount === undefined ? cards : cards.slice(0, _visibleProductCount);
    // Numbered off the full result set, not the visible slice, so a resize that changes
    // how many cards fit never re-points an existing id at a different row. It leaves
    // gaps, which nothing reads.
    return [
      ...visibleCards.map((product, i) => ({
        kind: "product" as const,
        id: `${ITEM_ID_PREFIX}${i}`,
        product,
        href: product.url ?? searchUrl(autosuggest.displayValue),
      })),
      {
        kind: "showAll" as const,
        id: `${ITEM_ID_PREFIX}${cards.length}`,
        href: searchUrl(autosuggest.displayValue),
      },
      ...suggestions.map((s, i) => ({
        kind: "suggestion" as const,
        id: `${ITEM_ID_PREFIX}${cards.length + 1 + i}`,
        term: s.query,
      })),
    ];
  });

  const isFetching = $derived(
    Boolean(autosuggest.suggestQuery?.isFetching) || Boolean(autosuggest.productsQuery?.isFetching),
  );

  const isEmpty = $derived(!isFetching && items.length === 0);

  const itemCount = $derived(items.length);

  const panelVisible = $derived(autosuggest.isOpen && !_dismissed);

  // The list shrinks on a resize or a narrower query, and a raw index left pointing past
  // the end would name an option that is no longer there.
  const highlightedIndex = $derived(_highlightedIndex < itemCount ? _highlightedIndex : -1);

  // `highlightedIndex` clamps on read, but the raw index has to be written back too: a
  // fit re-measure that shrinks the list and then regrows it — an iOS keyboard opening
  // and closing — would otherwise resurrect the old index at a different row.
  function clampHighlight(): void {
    if (_highlightedIndex >= itemCount) _highlightedIndex = -1;
  }

  const highlightedItemId = $derived(
    highlightedIndex >= 0 ? items[highlightedIndex]?.id : undefined,
  );

  return {
    get displayValue(): string {
      return autosuggest.displayValue;
    },
    setQuery(v: string): void {
      _dismissed = false;
      // A new query is a new list, so the old highlight goes: otherwise Enter after
      // refining navigates to whatever card was highlighted instead of submitting what
      // was typed, and Left/Right never hand the caret back. Callers that select an item
      // read it before calling this.
      _highlightedIndex = -1;
      autosuggest.query = v;
    },
    setQueryImmediately(v: string): void {
      _dismissed = false;
      autosuggest.setQueryImmediately(v);
    },
    seedDisplayValue(v: string): void {
      autosuggest.seedDisplayValue(v);
    },

    get panelVisible(): boolean {
      return panelVisible;
    },

    get highlightedIndex(): number {
      return highlightedIndex;
    },
    get highlightedItemId(): string | undefined {
      return highlightedItemId;
    },
    get itemCount(): number {
      return itemCount;
    },
    moveHighlight(delta: 1 | -1): void {
      if (itemCount === 0) return;
      // -1 is "nothing highlighted", which sits *before* the first item — so arrowing
      // back from it belongs at the end of the list, not one short of it.
      if (highlightedIndex < 0) {
        _highlightedIndex = delta === 1 ? 0 : itemCount - 1;
        return;
      }
      _highlightedIndex = (highlightedIndex + delta + itemCount) % itemCount;
    },

    /** How many product cards the shell has room for; `undefined` lifts the cap. */
    setVisibleProductCount(n: number | undefined): void {
      _visibleProductCount = n;
      clampHighlight();
    },
    /** How many query suggestions the shell shows; `undefined` lifts the cap. */
    setVisibleSuggestionCount(n: number | undefined): void {
      _visibleSuggestionCount = n;
      clampHighlight();
    },
    resetHighlight(): void {
      _highlightedIndex = -1;
    },

    /**
     * Products the query returned, before the shell's fit cap — the shells watch it to
     * know a new result set has landed. Reading the capped `items` instead would make
     * the fit measurement depend on its own output.
     */
    get productCount(): number {
      return cards.length;
    },

    get items(): AutosuggestItem[] {
      return items;
    },
    get highlightedItem(): AutosuggestItem | undefined {
      return highlightedIndex >= 0 ? items[highlightedIndex] : undefined;
    },
    dismiss(): void {
      _dismissed = true;
      _highlightedIndex = -1;
    },
    /**
     * Re-shows a panel a dismissal hid, provided the query still qualifies. Desktop only:
     * the mobile field exists only while the sheet is open, and reopening it is the
     * equivalent gesture there.
     */
    reopen(): void {
      _dismissed = false;
    },
    /**
     * Escape, from either shell. The first press hides the panel and keeps what was
     * typed; a second — with the panel already down — clears the field.
     *
     * Returns which step it took, because the shells act on it differently: the mobile
     * sheet closes on the press that clears, so MobileSearchShell lets that one travel
     * on to SlideOverDialog and stops the one that only dismissed.
     */
    escape(): "dismissed" | "cleared" {
      _highlightedIndex = -1;
      if (panelVisible) {
        _dismissed = true;
        return "dismissed";
      }
      autosuggest.setQueryImmediately("");
      _dismissed = false;
      return "cleared";
    },
    /**
     * The term Bloomreach silently corrected the query to, or null. Both shells surface
     * it: without the line, a corrected query looks like the panel ignored what was typed.
     */
    get autocorrectQuery(): string | null {
      return autosuggest.autocorrectQuery;
    },
    get isFetching(): boolean {
      return isFetching;
    },
    get isEmpty(): boolean {
      return isEmpty;
    },
  };
}
